import type { TextVariants } from "@/components/ui/baseUi/text"
import { Text } from "@/components/ui/baseUi/text"
import type { PayoutDetailsEntity } from "@/modules/finances/financesPayout/entities/payoutEntity"
import { cn } from "@/modules/lib/utils"
import { CheckmarkIcon } from "@images/svgIcons/checkmarkIcon"
import { PendingIcon } from "@images/svgIcons/pendingIcon"
import { ErrorIcon } from "@images/svgIcons/status/errorIcon"
import { InTransitFailedIcon } from "@images/svgIcons/status/inTransitFailedIcon"
import { InTransitIcon } from "@images/svgIcons/status/inTransitIcon"
import { PayoutTransferStatus } from "@modules/finances/types/financesType"
import type { VariantProps } from "class-variance-authority"

type PayoutStatusWithIconProps = {
  payout: PayoutDetailsEntity
  size?: number
  color?: string
  fontStyle?: VariantProps<typeof TextVariants>["fontStyle"]
  className?: string
  textClassName?: string
} & React.ComponentProps<"div">

export function PayoutStatusWithIcon({
  payout,
  size,
  color,
  fontStyle,
  textClassName,
  className,
  ...props
}: PayoutStatusWithIconProps) {
  const getIcon = () => {
    switch (payout.status) {
      case PayoutTransferStatus.PAID:
        return (
          <CheckmarkIcon
            style={{ width: size ?? "24px", height: size ?? "24px" }}
            color="currentColor"
            className={cn("text-textSuccess", color)}
          />
        )
      case PayoutTransferStatus.IN_TRANSIT:
        return <InTransitIcon size={size ?? 24} color="currentColor" className={cn("text-textSuccess", color)} />
      case PayoutTransferStatus.CANCELED:
        return (
          <ErrorIcon
            style={{ width: size ?? "24px", height: size ?? "24px" }}
            color="currentColor"
            className={cn("text-textDefault-body", color)}
          />
        )
      case PayoutTransferStatus.PENDING:
        return (
          <PendingIcon
            style={{ width: size ?? "24px", height: size ?? "24px" }}
            color="currentColor"
            className={cn("text-custom-orange-500", color)}
          />
        )
      case PayoutTransferStatus.FAILED:
        return <InTransitFailedIcon size={size ?? 24} color="currentColor" className={cn("text-red-500", color)} />
      default:
        return null
    }
  }

  return (
    <div className={cn("flex items-center gap-1", className)} {...props}>
      {getIcon()}
      <Text className={cn("text-textDefault-heading", textClassName)} fontStyle={fontStyle ?? "BM"}>
        {payout.getPayoutStatusWord()}
      </Text>
    </div>
  )
}
